/** @file dragSession: a pointer drag on a gizmo handle measured as a world-space gesture (pure). */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { screenDeltaToWorld } from '@zilo/pose3d-render-core/camera/screenDeltaToWorld';
import { screenOf } from '@zilo/pose3d-render-core/camera/screenOf';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { GizmoModel } from './gizmoModel';
import type { GizmoHandle } from './gizmoShape';
import { shortestYawDelta } from './shortestYawDelta';
import type { WorldDrag } from './viewportDrag';

/** What a drag starts from: the gesture kind, the handle (3 = centre, free; 4..6 = planes), the gizmo, view, pointer. */
export type DragStart = { kind: 'rotate' | 'move' | 'scale'; axis: GizmoHandle; model: GizmoModel; view: View; pointer: [number, number] };

/** A running drag: `update(pointer)` gives the gesture from the start to `pointer` (target pixels). */
export type DragSession = { readonly start: DragStart; update: (pointer: [number, number]) => WorldDrag };

const DEG = 180 / Math.PI;
/** Below this |axis . back| a ring is seen edge-on and turns by pointer travel instead of pointer angle. */
const EDGE_ON = 0.2;

/** Below this |sin| between the screen images of a plane's two axes the plane is edge-on and does not move. */
const EDGE_ON_PLANE = 0.05;

/** The handle axis in world space (the centre and the planes have none). */
const axisOf = (s: DragStart): Vec3 | null => (s.axis > 2 ? null : s.model.axes[s.axis as 0 | 1 | 2]);

/** Screen offset (px) that one world unit along `axis` makes. */
function screenAxis(s: DragStart, axis: Vec3): [number, number] {
  const o = s.model.origin;
  const a = screenOf(o, s.view);
  const b = screenOf([o[0] + axis[0], o[1] + axis[1], o[2] + axis[2]], s.view);
  return [b[0] - a[0], b[1] - a[1]];
}

/** Rotation: accumulated pointer angle about the projected origin, signed by whether the axis faces the viewer. */
function rotation(s: DragStart): (p: [number, number]) => WorldDrag {
  const axis = axisOf(s) ?? s.view.basis.back;
  const facing = axis[0] * s.view.basis.back[0] + axis[1] * s.view.basis.back[1] + axis[2] * s.view.basis.back[2];
  const o = screenOf(s.model.origin, s.view);
  const angle = (p: [number, number]): number => Math.atan2(p[1] - o[1], p[0] - o[0]) * DEG;
  const [ax, ay] = screenAxis(s, axis);
  const len = Math.hypot(ax, ay) || 1;
  let prev = angle(s.pointer);
  let total = 0;
  return (p) => {
    if (Math.abs(facing) < EDGE_ON) {
      const along = ((p[0] - s.pointer[0]) * -ay + (p[1] - s.pointer[1]) * ax) / len;
      return { kind: 'rotate', axis, deltaDeg: along };
    }
    const now = angle(p);
    total += shortestYawDelta(prev, now);
    prev = now;
    return { kind: 'rotate', axis, deltaDeg: -total * Math.sign(facing) };
  };
}

/** Move in the plane across axis `n`: the pointer travel solved over the screen images of the other two axes. */
function planar(s: DragStart, n: number): (p: [number, number]) => WorldDrag {
  const u = s.model.axes[(n + 1) % 3] ?? [0, 0, 0];
  const v = s.model.axes[(n + 2) % 3] ?? [0, 0, 0];
  const [ux, uy] = screenAxis(s, u);
  const [vx, vy] = screenAxis(s, v);
  const det = ux * vy - uy * vx;
  const flat = Math.abs(det) <= EDGE_ON_PLANE * Math.hypot(ux, uy) * Math.hypot(vx, vy);
  return (p) => {
    const dx = p[0] - s.pointer[0];
    const dy = p[1] - s.pointer[1];
    const a = flat ? 0 : (dx * vy - dy * vx) / det;
    const b = flat ? 0 : (ux * dy - uy * dx) / det;
    return { kind: 'move', delta: [a * u[0] + b * v[0] + 0, a * u[1] + b * v[1] + 0, a * u[2] + b * v[2] + 0] };
  };
}

/** Move: along the handle axis (the pointer travel projected on its screen direction), in a plane, or the view plane. */
function translation(s: DragStart): (p: [number, number]) => WorldDrag {
  if (s.axis > 3) return planar(s, s.axis - 4);
  const axis = axisOf(s);
  return (p) => {
    const dx = p[0] - s.pointer[0];
    const dy = p[1] - s.pointer[1];
    if (axis === null) return { kind: 'move', delta: screenDeltaToWorld(dx, dy, s.view) };
    const [ax, ay] = screenAxis(s, axis);
    const len2 = ax * ax + ay * ay;
    const t = len2 < 1e-9 ? 0 : (dx * ax + dy * ay) / len2;
    return { kind: 'move', delta: [axis[0] * t, axis[1] * t, axis[2] * t] };
  };
}

/** Scale: the pointer's distance from the projected origin over its distance at the start. */
function scaling(s: DragStart): (p: [number, number]) => WorldDrag {
  const o = screenOf(s.model.origin, s.view);
  const d0 = Math.max(1, Math.hypot(s.pointer[0] - o[0], s.pointer[1] - o[1]));
  return (p) => ({ kind: 'scale', ratio: Math.hypot(p[0] - o[0], p[1] - o[1]) / d0 });
}

/** One session per pointer-down on a handle; rotation accumulates across the +-180 degree seam (never a 350 spin). */
export function dragSession(start: DragStart): DragSession {
  const measure = start.kind === 'rotate' ? rotation(start) : start.kind === 'move' ? translation(start) : scaling(start);
  return { start, update: measure };
}
